import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Input, CustomSelect } from '../../shared';
import useOperatingMarkets from '../../../hooks/useOperatingMarkets';

/** Strips letters and symbols; keeps digits and common phone formatting. */
const sanitizePhoneInput = (value) =>
  String(value ?? '').replace(/[^\d+()\s.-]/g, '');

const CUSTOMER_TYPE_OPTIONS = [
  { value: 'individual', label: 'Individual' },
  { value: 'business', label: 'Business' },
];

const ContactInfoForm = ({
  bookingData,
  errors,
  onInputChange,
  user,
  requiredFields = [],
}) => {
  const { options: countryOptions, isLoading: marketsLoading, isEmpty } = useOperatingMarkets({
    bookableOnly: true,
  });
  const isBusiness = bookingData.customerType === 'business';
  const req = (field) => requiredFields.includes(field);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-black">
          Contact Information
        </h2>
        {user && (
          <div className="flex items-center text-sm text-black bg-gray-100 border border-black px-3 py-1.5 rounded-full">
            <CheckCircle className="w-4 h-4 mr-1.5" />
            Auto-filled
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="First Name *"
          type="text"
          value={bookingData.contactInfo.firstName}
          onChange={(e) => onInputChange('contactInfo.firstName', e.target.value)}
          placeholder="Enter your first name"
          error={errors['contactInfo.firstName'] || ''}
        />
        <Input
          label="Last Name *"
          type="text"
          value={bookingData.contactInfo.lastName}
          onChange={(e) => onInputChange('contactInfo.lastName', e.target.value)}
          placeholder="Enter your last name"
          error={errors['contactInfo.lastName'] || ''}
        />
        <Input
          label="Email *"
          type="email"
          value={bookingData.contactInfo.email}
          onChange={(e) => onInputChange('contactInfo.email', e.target.value)}
          placeholder="Enter your email"
          error={errors['contactInfo.email'] || ''}
        />
        <Input
          label="Phone *"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={bookingData.contactInfo.phone}
          onChange={(e) =>
            onInputChange('contactInfo.phone', sanitizePhoneInput(e.target.value))
          }
          placeholder="Enter your phone number"
          error={errors['contactInfo.phone'] || ''}
        />
        <CustomSelect
          label={req('billing_country') ? 'Billing market *' : 'Billing market'}
          value={bookingData.billingCountry || ''}
          onChange={(value) => onInputChange('billingCountry', value)}
          options={countryOptions}
          placeholder={marketsLoading ? 'Loading markets…' : 'Select market'}
          error={errors.billingCountry || (isEmpty ? 'No markets are open for bookings' : '')}
          disabled={marketsLoading || isEmpty}
          size="md"
        />
        <CustomSelect
          label={req('customer_type') ? 'Customer Type *' : 'Customer Type'}
          value={bookingData.customerType || 'individual'}
          onChange={(value) => onInputChange('customerType', value)}
          options={CUSTOMER_TYPE_OPTIONS}
          error={errors.customerType || ''}
          size="md"
        />
        {(isBusiness || req('customer_tax_id')) && (
          <div className="md:col-span-2">
            <Input
              label={req('customer_tax_id') ? 'Business Tax ID *' : 'Business Tax ID'}
              type="text"
              value={bookingData.customerTaxId || ''}
              onChange={(e) => onInputChange('customerTaxId', e.target.value)}
              placeholder="VAT / GST / EIN"
              error={errors.customerTaxId || ''}
            />
          </div>
        )}
      </div>
      {user && (
        <div className="mt-6 p-4 bg-gray-100 border border-gray-300 rounded-lg">
          <p className="text-sm text-black">
            <strong>Note:</strong> Your contact information has been automatically
            filled from your profile. You can edit any field if needed.
          </p>
        </div>
      )}
    </div>
  );
};

export default ContactInfoForm;
